import {Typography, css, useTheme} from '@mui/material';
import {ButtonType, DlMuiButton, GetStylesFunction} from '@wallaby-ui/core';
import React from 'react';
import {FormattedMessage} from 'react-intl';

type Props = {
  hasSorting?: boolean;
  handleResetSorting?: () => void;
  hasFilters?: boolean;
  handleResetFilters?: () => void;
  resultCount?: number;
  buttonType?: ButtonType;
  align?: 'flex-start' | 'center' | 'flex-end';
  buttonsWidth?: string;
  justifyContent?: 'space-around' | 'space-between' | 'flex-start' | 'flex-end' | 'stretch';
  marginBottom?: string;
  marginLeft?: string;
  useVisibility?: boolean;
  width?: string;
  hasXlsxExport?: boolean;
  handleXlsxExport?: () => void;
  /** Export cannot run right now; the tooltip (a message key) says why. */
  xlsxExportDisabled?: boolean;
  xlsxExportDisabledTooltip?: string;
  hasPdfExport?: boolean;
  handlePdfExport?: () => void;
  /** Reset filtrů vlastní jiná část stránky (hlavička panelu Filtry) — zde se nerenderuje. */
  hideResetFilters?: boolean;
};
export const LightSelectedFilters: React.FC<Props> = (props) => {
  const {
    hasSorting,
    handleResetSorting,
    hasFilters,
    handleResetFilters,
    resultCount,
    buttonType = 'chip-filled',
    align = 'flex-start',
    buttonsWidth = 'auto',
    justifyContent = 'space-between',
    marginBottom = 'var(--dlSelectedFiltersContainerMarginBottom)',
    marginLeft = '0px',
    useVisibility,
    width = '100%',
    hasXlsxExport,
    handleXlsxExport,
    xlsxExportDisabled,
    xlsxExportDisabledTooltip,
    hasPdfExport,
    handlePdfExport,
    hideResetFilters,
  } = props;
  const theme = useTheme();
  const styles = getStyles({
    theme,
    params: {
      align,
      buttonsWidth,
      justifyContent,
      marginBottom,
      marginLeft,
      useVisibility,
      width,
    },
  });
  if (!(hasSorting || hasFilters || hasXlsxExport || hasPdfExport) && !useVisibility) return null;
  return (
    <div data-name={'mui-selected-filters--wrapper'} css={styles.wrapper}>
      <div data-name={'mui-selected-filters--buttons-wrapper'} css={styles.buttonsWrapper}>
        {(hasSorting || useVisibility) && (
          <DlMuiButton
            type={buttonType}
            title={'sort.reset.text'}
            css={styles.button}
            onClick={handleResetSorting}
            data-hidden={!hasSorting}
          />
        )}
        {!hideResetFilters && (hasFilters || useVisibility) && (
          <DlMuiButton
            type={buttonType}
            title={'filter.reset.text'}
            css={styles.button}
            onClick={handleResetFilters}
            data-hidden={!hasFilters}
          />
        )}
        {(hasXlsxExport || useVisibility) && (
          <DlMuiButton
            type={buttonType}
            title={'xlsx.export.title'}
            css={styles.button}
            onClick={handleXlsxExport}
            disabled={xlsxExportDisabled}
            disabledTooltip={xlsxExportDisabledTooltip}
            data-hidden={!hasXlsxExport}
          />
        )}
        {(hasPdfExport || useVisibility) && (
          <DlMuiButton
            type={buttonType}
            title={'pdf.export.title'}
            css={styles.button}
            onClick={handlePdfExport}
            data-hidden={!hasPdfExport}
          />
        )}
        {hasFilters && resultCount !== undefined && resultCount !== null && (
          <Typography data-name={'mui-selected-filters--search-results-typography'} variant={'caption'}>
            <FormattedMessage
              data-name={'mui-selected-filters--search-results'}
              id={'common.filters.searchResults'}
              defaultMessage={'of {results}'}
              description={'label Page number'}
              values={{results: <>{resultCount}</>}}
            />
          </Typography>
        )}
      </div>
    </div>
  );
};

LightSelectedFilters.displayName = 'LightSelectedFilters';

const getStyles: GetStylesFunction = (options) => {
  const buttonFlex = options?.params?.justifyContent === 'stretch' ? 'flex: 1 1 0;' : '';
  const displayNoneOrVisiblityHidden = options?.params?.useVisibility ? 'visibility: hidden;' : 'display: none;';
  return {
    emptyWrapper: css`
      width: 100%;
    `,
    wrapper: css`
      width: ${options?.params?.width};
      display: flex;
      justify-content: ${options?.params?.align};
      align-items: center;
      margin-bottom: ${options?.params?.marginBottom};
    `,
    buttonsWrapper: css`
      display: inline-flex;
      justify-content: ${options?.params?.justifyContent};
      align-items: center;
      width: ${options?.params?.buttonsWidth};
      margin-left: ${options?.params?.marginLeft};
      column-gap: 6px;
    `,
    button: css`
      ${buttonFlex}
      &[data-hidden='true'] {
        ${displayNoneOrVisiblityHidden}
      }
    `,
  };
};
